<script lang="ts">
import { defineComponent } from 'vue';
import {
  type AvailableLanguage,
  type ItemTranslation,
  useTranslationsStore,
} from '@/stores/translations';
import { mapActions, mapState, mapWritableState } from 'pinia';
import Pagination from '@/components/pagination.vue';
import IconSports from '@/helpers/icon-pack-sports.vue';
import { getIconAlias } from '@/utils/transform';
import Headline from '@/components/headline.vue';
import ClipboardIcon from '@/components/clipboard-icon.vue';
import { useAppStore } from '@/stores/app';

export default defineComponent({
  name: 'translations',
  components: { ClipboardIcon, Pagination, IconSports, Headline },
  computed: {
    ...mapState(useTranslationsStore, [
      'translations',
      'translationsCount',
      'languages',
      'types',
      'translationsLoading',
    ]),
    ...mapWritableState(useTranslationsStore, ['translationsOffset', 'translationsPageSize']),
    originalWidthStyle() {
      const w = { selection: 25 }[this.typePostLoad] || 45;
      return `width: ${w}%`;
    },
    translationWidthStyle() {
      const w = { selection: 25 }[this.typePostLoad] || 50;
      return `width: ${w}%`;
    },
    hasSportsName() {
      return ['fixture'].includes(this.typePostLoad);
    },
    hasSportsIds() {
      return ['market'].includes(this.typePostLoad);
    },
    hasMarketName() {
      return ['selection'].includes(this.typePostLoad);
    },
    hasSelectionName() {
      return ['selection'].includes(this.typePostLoad);
    },
  },
  data() {
    return {
      type: 'fixture',
      typePostLoad: 'fixture',
      language: 'ru' as AvailableLanguage,
      editId: null as null | string,
      editValue: null as null | string,
      showApproved: false as null | boolean,
      showTranslated: true,
      clipboardItemId: null as null | string,
      clipboardItems: [
        '(женщины)',
        '(резерв)',
        '(пары)',
        'квалификация',
        'смешанные пары',
        'молодежная',
      ],
    };
  },
  methods: {
    ...mapActions(useTranslationsStore, [
      'fetchTranslations',
      'updateTranslation',
      'removeTranslation',
      'deleteTranslation',
      'toggleApproval',
    ]),
    ...mapActions(useAppStore, ['copyToClipboard']),
    getIconAlias,
    fetchTranslationsWithFilters() {
      const filters: { [filters: string]: string | number | boolean } = {
        type: this.type,
        lang: this.language,
        translated: this.showTranslated,
      };

      if (this.showApproved !== null) {
        filters.approved = this.showApproved;
      }

      return this.fetchTranslations(filters);
    },
    openEdit(translation: ItemTranslation) {
      this.editId = translation.id;
      this.editValue = translation[this.language];
      this.$nextTick(() => {
        if (this.$refs?.descriptionInput) {
          // @ts-expect-error $refs might be undefined, but we checked it above
          this.$refs?.descriptionInput[0].$el.focus();
        }
      });
    },
    onEditKeyUp(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        const translationRecord = this.translations?.find((t) => t.id === this.editId);

        if (translationRecord && this.editValue?.length) {
          this.updateTranslation(translationRecord.id, {
            lang: this.language as AvailableLanguage,
            translation: this.editValue,
          }).then(() => {
            translationRecord[this.language] = this.editValue as string;
            this.editId = null;
            this.$nextTick(() => {
              this.editValue = null;
            });
          });
        }
      }
    },
    onFocusOut({ currentTarget, relatedTarget }: {  currentTarget: HTMLElement, relatedTarget: HTMLElement }) {
      if (currentTarget.contains(relatedTarget)) return;

      this.editId = null;
      this.editValue = null;
    },
    flipTranslationsPage(newOffset: number) {
      this.translationsOffset = newOffset;
      this.fetchTranslationsWithFilters();
    },
    approved(translation: ItemTranslation) {
      return translation.validations && translation.validations[this.language];
    },
    filterApproved(value: null | boolean) {
      this.showApproved = value;
      this.fetchTranslationsWithFilters();
    },
    filterTranslated(value: boolean) {
      this.showTranslated = value;
      if (!this.showTranslated) {
        this.showApproved = null;
      }
      this.fetchTranslationsWithFilters();
    },
    updateMainFilter() {
      this.fetchTranslationsWithFilters().then(() => {
        this.typePostLoad = this.type;
      });
    },
    copyToClipboardComponent(text: string, id: string) {
      this.copyToClipboard(text).then(() => {
        this.clipboardItemId = id;
      });
    },
    async toggle(translation: ItemTranslation) {
      return this.toggleApproval(translation.id, this.language).then(() => {
        if (translation.validations) {
          translation.validations[this.language] = !translation?.validations[this.language];
        } else {
          translation.validations = { [this.language]: true };
        }
      });
    },
    async approveAll() {
      if (this.translations?.length) {
        this.translations.forEach((translation) => this.toggle(translation));
      }
    },
    async remove(translation: ItemTranslation) {
      return this.removeTranslation(this.language, translation.id).then(() => {
        this.fetchTranslationsWithFilters();
      });
    },
    async deletee(translation: ItemTranslation) {
      return this.deleteTranslation(this.type, translation.id).then(() => {
        this.fetchTranslationsWithFilters();
      });
    },
  },
  mounted() {
    if (!this.translations) {
      this.fetchTranslationsWithFilters();
    }
  },
});
</script>

<template>
  <template v-if="!translations">
    <div class="custom-background" style="height: 90vh">Нет записей или доступ ограничен</div>
  </template>
  <template v-else>
    <teleport to="#title">
      <headline class="mx-4 my-2">Переводы</headline>
    </teleport>
    <section class="mx-1 mb-3 relative">
      <select v-model="language" @change="updateMainFilter" class="mx-2 p-2">
        <option v-for="language in languages" :key="language.value" :value="language.value">
          {{ language.name }}
        </option>
      </select>
      <select v-model="type" @change="updateMainFilter" class="mx-2 p-2">
        <option v-for="type in types" :key="type.value" :value="type.value">
          {{ type.name }}
        </option>
      </select>
      <button
        @click="filterTranslated(false)"
        :class="{
          'p-2 border border-gray-300 dark:border-gray-700 rounded-l-lg': true,
          'bg-gray-300 dark:bg-gray-700': !showTranslated,
          'text-gray-500': showTranslated,
        }"
      >
        Непереведенные
      </button>
      <button
        @click="filterTranslated(true)"
        :class="{
          'p-2 border border-gray-300 dark:border-gray-700 rounded-r-lg  mr-2': true,
          'bg-gray-300 dark:bg-gray-700': showTranslated,
          'text-gray-500': !showTranslated,
        }"
      >
        Переведенные
      </button>
      <template v-if="showTranslated">
        <button
          @click="filterApproved(null)"
          :class="{
            'p-2 border border-gray-300 dark:border-gray-700 border-r-0 rounded-l-lg': true,
            'bg-gray-300 dark:bg-gray-700': showApproved === null,
            'text-gray-500': showApproved !== null,
          }"
        >
          Все
        </button>
        <button
          @click="filterApproved(true)"
          :class="{
            'p-2 border border-gray-300 dark:border-gray-700': true,
            'bg-gray-300 dark:bg-gray-700': showApproved === true,
            'text-gray-500': showApproved !== true,
          }"
        >
          Одобренные
        </button>
        <button
          @click="filterApproved(false)"
          :class="{
            'p-2 border border-gray-300 dark:border-gray-700 border-l-0 rounded-r-lg': true,
            'bg-gray-300 dark:bg-gray-700': showApproved === false,
            'text-gray-500': showApproved !== false,
          }"
        >
          Неодобренные
        </button>
      </template>
    </section>
    <section class="relative mx-3">
      <transition name="fade">
        <div v-if="translationsLoading" class="table-loader">
          <i class="pi pi-spinner animate-spin text-gray-200" style="font-size: 5em" />
        </div>
      </transition>
      <table v-if="translations" class="basic-table__unaligned" style="width: 100%">
        <thead>
          <tr>
            <th v-if="hasSportsIds" style="min-width: 100px">&nbsp;</th>
            <th v-if="hasSportsName"></th>
            <th v-if="hasMarketName" style="text-align: left">Пример маркета</th>
            <th v-if="hasSelectionName" style="text-align: left">Пример кэфа</th>
            <th class="text-left" :style="originalWidthStyle">
              <span class="text-lg">Оригинальный текст</span>
            </th>
            <th class="text-left" :style="translationWidthStyle">
              <div class="flex">
                <span class="text-lg">Перевод на {{ language.toUpperCase() }}</span>
              </div>
            </th>
            <!--            <th>L</th>-->
            <th class="text-center" style="min-width: 100px">
              <!--              <span class="text-lg">Одобрен</span>-->
              <span class="cursor-pointer mx-2" @click="approveAll">
                <i class="pi pi-check" style="font-size: 1.7em"></i>
              </span>
              <span class="cursor-pointer mx-2" @click="fetchTranslationsWithFilters">
                <i class="pi pi-refresh" style="font-size: 1.7em" />
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="translation in translations" :key="translation.id" class="row__hoverable">
            <td v-if="translation.sport_ids">
              <span class="flex flex-wrap gap-1">
                <IconSports
                  cssClass="fill-gray-500"
                  v-for="id in translation.sport_ids"
                  :id="getIconAlias(id)"
                  :height="20"
                  :width="20"
                />
              </span>
            </td>
            <td v-if="hasSportsName">
              <IconSports
                :id="getIconAlias(translation.sport_id || 0)"
                :height="20"
                :width="20"
                :title="translation.sport_name"
                cssClass="fill-gray-500"
              />
            </td>
            <td v-if="hasMarketName">
              {{ translation.market_name }}
            </td>
            <td v-if="hasSelectionName">
              {{ translation.selection_name }}
            </td>
            <td class="p-2 relative">
              <i
                :class="{
                  'pi pi-copy absolute right-2 text-xl cursor-pointer': true,
                  'text-yellow-500': clipboardItemId === translation.id,
                }"
                @click="copyToClipboardComponent(translation.en, translation.id)"
              />
              <span class="text-lg">
                {{ translation.en }}
              </span>
            </td>
            <td class="cursor-pointer relative">
              <div
                v-if="editId !== translation.id"
                @click="openEdit(translation)"
                class="cursor-pointer text-lg px-1 py-1"
              >
                {{ translation[language] }}
              </div>
              <template v-else>
                <div @focusout="onFocusOut">
                  <div class="absolute bottom-12 left-0 right-0 text-right mr-1">
                    <clipboard-icon
                      v-for="(item, i) in clipboardItems"
                      class="ml-auto mr-1 bg-gray-600 px-2 py-1 rounded"
                      :hide-icon="true"
                      :text="item"
                      :tabindex="i"
                    >
                      <span class="text-sm font-light">{{ item }}</span>
                    </clipboard-icon>
                  </div>
                  <InputText
                    ref="descriptionInput"
                    class="text-left text-lg px-1 py-1"
                    style="width: 100%"
                    v-model="editValue"
                    @keyup="onEditKeyUp"
                  />
                </div>
              </template>
            </td>
            <!--            <td>{{ translation.validations }}</td>-->
            <td class="text-center">
              <span class="cursor-pointer" @click="toggle(translation)">
                <i
                  :class="{
                    'pi text-xl': true,
                    'pi-check-circle text-green-500': approved(translation),
                    'pi-check': !approved(translation),
                  }"
                />
              </span>
              <template v-if="['fixture', 'competition'].includes(type)">
                <span class="cursor-pointer text-center ml-2" @click="remove(translation)">
                  <i class="pi pi-times text-xl text-yellow-500" />
                </span>
                <span class="cursor-pointer text-center ml-2" @click="deletee(translation)">
                  <i class="pi pi-trash text-xl text-red-500" />
                </span>
              </template>
            </td>
          </tr>
          <tr
            v-if="translations.length < translationsPageSize"
            v-for="_ in Array(translationsPageSize - translations.length)"
          >
            <td colspan="1000" class="p-2">
              <div class="text-lg p-1">&nbsp;</div>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="text-center">
        <Pagination
          :offset="translationsOffset"
          :size="translationsPageSize"
          :count="translationsCount"
          @flip="flipTranslationsPage"
        />
      </div>
    </section>
  </template>
</template>

<style scoped lang="scss">
.row__hoverable {
  @apply text-gray-500;
  &:hover {
    @apply text-gray-800 dark:text-gray-200 bg-gray-200 dark:bg-gray-800;
  }
}
</style>
